import { useState, useEffect, ReactElement, ChangeEvent, useCallback, memo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Input, notification } from 'antd';
import { useTranslation } from 'react-i18next';
import { NotificationPlacement } from 'antd/es/notification/interface';

import { Auth_Login_Request, remote_Auth_login } from '~/common/typescript/apis/AuthAPIs';
import * as S from './Login.styles';
import Routes from '~/routes/routes';
import FormItemWrapper from '~/components/common/FormItemWrapper/FormItemWrapper';
import { ADMIN_URL } from '~/config/config';

import MailImage from '~/assets/svg/mail.svg';
import KeyImage from '~/assets/svg/key_password.svg';
import OpenEye from '~/assets/svg/open_eye.svg';
import CloseEye from '~/assets/svg/close_eye.svg';

import useDebounce from '~/utils/debounce';

/**
 * /login?goto=https://portal.tlgeo.xyz
 */
const LoginScreen = (): ReactElement => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const openNotification = useCallback(
    (
      placement: NotificationPlacement,
      method: 'success' | 'error',
      message: string,
      description: string,
      duration: number = 3,
    ) => {
      notification[method]({
        message: message,
        description: description,
        placement,
        duration: duration,
      });
    },
    [],
  );
  const [isLoading, setLoading] = useState<boolean>(false);
  const [identity, setIdentity] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [typePassword, setTypePassword] = useState<'text' | 'password'>('password');
  const [isErrorIdentity, setErrorIdentity] = useState<boolean>(false);
  const [isErrorPassword, setErrorPassword] = useState<boolean>(false);
  const [isValid, setValid] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const navigate = useNavigate();
  const emailDebounce = useDebounce(identity, 800);

  const handleEditIdentity = (e: ChangeEvent<HTMLInputElement>) => {
    setIdentity(e.target.value);
    setErrorIdentity(false);
    setErrorMessage('');
  };
  const handleEditPassword = (e: ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
    setErrorPassword(false);
    setErrorMessage('');
  };

  const handleBlurPassword = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.value.trim() === '') {
      setErrorMessage(t('required.password') || 'Vui lòng nhập mật khẩu');
      setErrorPassword(true);
      setValid(false);
    } else {
      setErrorPassword(false);
    }
  }, [t]);

  const do_login = useCallback(() => {
    const bodyLogin: Auth_Login_Request = {
      identifier: identity,
      password: password,
    };
    // call api to server
    setLoading(true);
    remote_Auth_login(bodyLogin)
      .then((res) => {
        localStorage.setItem('jwt', res.jwt);
        if (res.jwt) {
          const gotoURL = searchParams.get('goto') || searchParams.get('redirect');
          if (gotoURL) {
            window.location.href = gotoURL;
          } else {
            navigate(Routes.PROJECT);
          }
        }
      })
      .catch((err) => {
        setLoading(false);
        console.error('login failed', err);
        if (err.code === 'ERR_BAD_REQUEST') {
          const desc = t('auth.login.fail.incorrect.description') || 'Tài khoản hoặc mật khẩu không chính xác';
          setErrorMessage(desc);
          openNotification(
            'top',
            'error',
            t('auth.login.fail.incorrect.message') || 'Đăng nhập thất bại',
            desc,
            2,
          );
        } else if (err.code === 'ERR_NETWORK') {
          openNotification('top', 'error', t('network_error_title') || 'Lỗi kết nối', t('network_error_description') || 'Không thể kết nối đến máy chủ', 2);
        } else {
          openNotification('top', 'error', 'Đăng nhập thất bại', err?.message || 'Có lỗi xảy ra', 2);
        }
      });
  }, [identity, password, navigate, searchParams, openNotification, t]);

  useEffect(() => {
    if (!isErrorIdentity && identity !== '' && password !== '') {
      setValid(true);
    } else {
      setValid(false);
    }
  }, [isErrorIdentity, identity, password]);

  const handleForgotPassword = () => {
    window.location.href = `${ADMIN_URL}/forgot-password?goto=${encodeURIComponent(window.location.href)}`;
  };

  const handleRegister = () => {
    window.location.href = `${ADMIN_URL}/register?goto=${encodeURIComponent(window.location.href)}`;
  };

  return (
    <S.Wrapper>
      <S.MethodLogin>
        <S.LoginForm>
          <S.FormField>
            <FormItemWrapper label={t('auth.login.placeholder.identifier') || 'Email hoặc số điện thoại'}>
              <Input
                prefix={<img src={MailImage} alt="mail" style={{ paddingRight: 4 }} />}
                value={identity}
                onChange={handleEditIdentity}
                spellCheck={false}
                size="large"
                placeholder={t('auth.login.placeholder.identifier') || 'Email hoặc số điện thoại'}
                status={isErrorIdentity ? 'error' : ''}
              />
            </FormItemWrapper>
            {errorMessage && <S.ErrorMessage id="error-identifier">{errorMessage}</S.ErrorMessage>}
          </S.FormField>

          <S.FormField>
            <FormItemWrapper label={t('auth.password') || 'Mật khẩu'}>
              <Input
                prefix={<img src={KeyImage} alt="password" style={{ paddingRight: 4 }} />}
                value={password}
                onChange={handleEditPassword}
                onBlur={handleBlurPassword}
                spellCheck={false}
                type={typePassword}
                size="large"
                placeholder={t('auth.login.placeholder.password') || 'Nhập mật khẩu'}
                status={isErrorPassword ? 'error' : ''}
                suffix={
                  <img
                    src={typePassword === 'password' ? OpenEye : CloseEye}
                    alt=""
                    onClick={() => {
                      setTypePassword(typePassword === 'password' ? 'text' : 'password');
                    }}
                    style={{ cursor: 'pointer' }}
                  />
                }
                onKeyUp={(e) => {
                  if (e.code === 'Enter') {
                    do_login();
                  }
                }}
              />
            </FormItemWrapper>
            <S.ErrorMessage id="error-password">{errorMessage}</S.ErrorMessage>
          </S.FormField>

          <S.AnotherAction>
            <S.ForgotPassword onClick={handleForgotPassword}>
              {t('auth.forgotPassword') || 'Quên mật khẩu?'}
            </S.ForgotPassword>
          </S.AnotherAction>

          <S.AuthAction>
            <S.ButtonLogin
              onClick={do_login}
              disabled={!isValid || isLoading}
              id="btn-login"
            >
              {isLoading ? (t('auth.logging_in') || 'Đang đăng nhập...') : (t('auth.login.name') || 'Đăng nhập')}
            </S.ButtonLogin>

            <S.RegisterPrompt>
              Chưa có tài khoản? <span onClick={handleRegister}>Đăng ký ngay</span>
            </S.RegisterPrompt>
          </S.AuthAction>
        </S.LoginForm>
      </S.MethodLogin>
    </S.Wrapper>
  );
};

export default memo(LoginScreen);
